/* eslint-disable */
import React, { useEffect } from 'react';
import { Pagination, Exports, Search, TreeIcon, SortIcon, CellContent } from './components';
import { customStyles } from './utils';
import {
    Table,
    Header,
    HeaderRow,
    Body,
    Row,
    HeaderCell,
} from '@table-library/react-table-library/table';

import { useTheme } from '@table-library/react-table-library/theme';

import { useSort, HeaderCellSort } from '@table-library/react-table-library/sort';

import { useTree } from '@table-library/react-table-library/tree';

import { usePagination } from '@table-library/react-table-library/pagination';
import { showData } from '../../helpers/isEmpty';
import {
    HeaderCellSelect,
    CellSelect,
    useRowSelect,
    SelectClickTypes,
    SelectTypes,
} from '@table-library/react-table-library/select';
import { noDataMessage } from '../../helpers/noDataMessage';
import { useTranslation } from 'react-i18next';

export default function DataTable({
    nodes = [],
    searchFunction,
    onlyTable,
    sortFns,
    header,
    COLUMNS,
    customColumnsForExport,
    searchWithTree = false,
    hasSort = true,
    additionalStyles,
    uniqKey,
    fileName,
    openedItemIds = [],
    extraBtns = () => null,
    additionalBox = () => null,
    hasTree,
    defaultSize,
    defaultSortKey,
    onlyPagination = false,
    onSelect = () => null,
    hasSelect,
    removeAllSelected = false,
    customeSearch,
    forceRun,
    isSingleSelect,
    pdfColumnSize,
    EXTRA_HEADER,
    headerHasCustomStyles,
    defaultSearch,
    customEmptyMessage,
    loadMore,
    loadMoreLoading,
    showLoadMoreBtn,
    exportAll,
    exportAllLoading,
    showViewAll,
    viewAllLink,
    notUseDefaultStyle,
    extraHeaders = null,
    banner = null,
    customContainerStyles = '',
    isSelectDisabled,
    hideAllSelect,
    rowClassName,
    onRowClick,
}) {
    const [search, setSearch] = React.useState(defaultSearch ?? '');
    const [data, setData] = React.useState({ nodes: [] });
    const { t } = useTranslation();

    /**
     * Clicking a row does whatever its action button does.
     *
     * The button stays — it is what tells a reader the row does anything at all — but a reader
     * who has understood that should not have to aim for it.
     *
     * Delegated from the body rather than taken from `Row`'s own `onClick`, which the table
     * library gates behind a check that the clicked element is an svg, path, DIV, SPAN or TD.
     * That allowlist leaves dead patches wherever a cell renders anything else — a bold delta, a
     * monospaced expression, a muted note — and a row that works everywhere except on its own
     * numbers is worse than one that does not work at all.
     *
     * Anything in the row that already does something keeps doing it: links, buttons, the select
     * checkbox and the tree toggle are left to their own handlers. A text selection is not a
     * click either, so dragging across a cell to copy a value does not open the row.
     */
    const handleRowClick = (event) => {
        if (!onRowClick) return;

        const target = event.target;
        if (typeof target?.closest !== 'function') return;
        if (target.closest('a, button, input, select, textarea, label, [role="button"]')) return;
        if (window.getSelection?.()?.toString()) return;

        const row = target.closest('tr[data-row-id]');
        if (!row) return;

        const find = (list) => {
            for (const item of list ?? []) {
                if (String(item[uniqKey ?? 'id']) === row.dataset.rowId) return item;
                const inChildren = find(item.nodes);
                if (inChildren) return inChildren;
            }
            return null;
        };

        const item = find(data.nodes);
        if (item) onRowClick(item, event);
    };

    useEffect(() => {
        if (searchWithTree) {
            const filtered = nodes
                .map((item) => {
                    let childrens = [];

                    if (item.nodes) {
                        childrens = item.nodes.filter((children) =>
                            children.name.toLowerCase().includes(search.toLowerCase()),
                        );
                    }

                    if (childrens.length) {
                        return { ...item, nodes: childrens };
                    }

                    return null;
                })
                .filter((x) => x);

            setData({ nodes: filtered });
        } else {
            // An empty search filters nothing. The default predicate matches on `name`, and
            // optional chaining makes the whole expression undefined for a row that has no such
            // field — so a table of rows keyed by anything else (a `measure`, a `key`) used to
            // drop every row and render as headers over nothing, even with the search box empty.
            const data = {
                nodes: searchFunction
                    ? searchFunction(nodes, search)
                    : !search
                      ? nodes
                      : nodes.filter((item) =>
                            item?.name?.toLowerCase().includes(search.toLowerCase()),
                        ),
            };
            setData(data);
        }
        pagination.fns.onSetPage(0);
    }, [search, nodes]);

    useEffect(() => {
        if (removeAllSelected) {
            select.fns.onRemoveAll();
        }
    }, [removeAllSelected]);

    const printRef = React.useRef();

    const pagination = usePagination(data, {
        state: {
            page: 0,
            size: defaultSize ?? 25,
        },
    });

    const defaultSizes = [25, 50, 100];
    const sizes = defaultSize
        ? defaultSizes.includes(defaultSize)
            ? defaultSizes
            : [defaultSize, 25, 50, 100]
        : [25, 50, 100];

    const select = useRowSelect(
        data,
        {
            onChange: onSelect,
        },
        {
            clickType: SelectClickTypes.ButtonClick,
            rowSelect: isSingleSelect ? SelectTypes.SingleSelect : SelectTypes.MultiSelect,
            buttonSelect: isSingleSelect ? SelectTypes.SingleSelect : SelectTypes.MultiSelect,
        },
    );

    const styles = additionalStyles ? { ...customStyles, ...additionalStyles } : customStyles;
    const theme = useTheme(styles);

    const sortState = {
        ids: ['1'],
    };

    if (defaultSortKey) {
        sortState.sortKey = defaultSortKey;
        sortState.reverse = true;
    }
    const sort = useSort(
        data,
        {
            state: sortState,
        },
        {
            sortIcon: {
                margin: '0px',
                iconDefault: <SortIcon />,
                iconUp: <SortIcon state="up" />,
                iconDown: <SortIcon state="down" />,
            },
            sortFns,
        },
    );

    const tree = useTree(
        data,
        {
            state: {
                ids: openedItemIds,
            },
        },
        {
            // clickType: TreeExpandClickTypes.ButtonClick,
            treeIcon: {
                margin: '12px',
                backgroundColor: 'red',
                noIconMargin: '36px',
                iconRight: <TreeIcon size="md" />,
                iconDown: <TreeIcon size="sm" active={true} type="down" />,
            },
            sortFns,
        },
    );

    return (
        <div
            className={`data-table-container ${notUseDefaultStyle ? '' : 'card-body'} ${
                customContainerStyles ?? ''
            }`}
        >
            <div className={`dt-header ${!header ? 'justify-content-end' : ''}`}>
                {header && <h3>{header}</h3>}
                <div className="d-flex gap-8 align-items-center">
                    {extraHeaders}
                    {forceRun && (
                        <div
                            className={`dt-exports d-flex justify-content-end ${
                                !header && onlyTable ? 'w-100' : ''
                            }`}
                            title="Refresh and clear cache"
                        >
                            <button
                                className="dt-exports__button with-refresh"
                                type="button"
                                onClick={forceRun}
                            >
                                <i className="icon-Frame-converted"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {banner}
            <div className="dt-actions__bar mb-2">
                {extraBtns(hasTree, tree)}
                {!onlyTable && (
                    <div className="dt-actions_default_bar">
                        {customeSearch ?? (
                            <Search
                                setSearch={setSearch}
                                value={search}
                                placeholder={t('table.search.placeholder')}
                            />
                        )}
                        <Exports
                            pdfColumnSize={pdfColumnSize}
                            data={data}
                            printRef={printRef}
                            COLUMNS={customColumnsForExport ?? COLUMNS}
                            fileName={fileName ?? header}
                        />
                    </div>
                )}
            </div>
            {additionalBox(hasTree, tree)}
            <div className="dt-table-body" ref={printRef} onClick={handleRowClick}>
                <Table
                    data={data}
                    theme={theme}
                    layout={{ fixedHeader: true, custom: true, horizontalScroll: true }}
                    sort={sort}
                    tree={tree}
                    pagination={(!onlyTable || onlyPagination) && pagination}
                    select={select}
                >
                    {(tableList) => (
                        <>
                            {EXTRA_HEADER && (
                                <Header>
                                    <HeaderRow>
                                        {EXTRA_HEADER.map((row) => (
                                            <HeaderCell
                                                key={row.key}
                                                pinLeft={row.pinLeft ?? false}
                                                style={{
                                                    ...(headerHasCustomStyles && row.style
                                                        ? row.style()
                                                        : {}),
                                                }}
                                                className={row?.className ?? ''}
                                                onClick={(e) => {
                                                    if (
                                                        row.infoLink &&
                                                        e.target.id === 'infoIcon'
                                                    ) {
                                                        window.open(row.infoLink);
                                                    }
                                                }}
                                            >
                                                {row.label}
                                                {row.infoLink ? (
                                                    <i
                                                        id="infoIcon"
                                                        title={row.info}
                                                        className="icon-info cursor-pointer"
                                                        style={{ marginLeft: '7px' }}
                                                    ></i>
                                                ) : (
                                                    row.info && (
                                                        <i
                                                            title={row.info}
                                                            className="icon-info cursor-pointer"
                                                            style={{ marginLeft: '7px' }}
                                                        ></i>
                                                    )
                                                )}
                                            </HeaderCell>
                                        ))}
                                    </HeaderRow>
                                </Header>
                            )}
                            <Header>
                                <HeaderRow>
                                    {hasSelect && !hideAllSelect && (
                                        <HeaderCellSelect
                                            disabled={true}
                                            style={{
                                                top: EXTRA_HEADER ? '38px' : '0px',
                                            }}
                                        />
                                    )}
                                    {hasSort
                                        ? COLUMNS.map((row) => (
                                              <HeaderCellSort
                                                  sortKey={row.key}
                                                  key={row.key}
                                                  pinLeft={row.pinLeft ?? false}
                                                  style={{
                                                      ...(headerHasCustomStyles && row.style
                                                          ? row.style()
                                                          : {}),
                                                      top: EXTRA_HEADER ? '38px' : '0px',
                                                  }}
                                                  onClick={(e) => {
                                                      if (
                                                          row.infoLink &&
                                                          e.target.id === 'infoIcon'
                                                      ) {
                                                          window.open(row.infoLink);
                                                      }
                                                  }}
                                              >
                                                  {row.label}
                                                  {row.infoLink ? (
                                                      <i
                                                          id="infoIcon"
                                                          title={row.info}
                                                          className="icon-info cursor-pointer"
                                                          style={{ marginLeft: '7px' }}
                                                      ></i>
                                                  ) : (
                                                      row.info && (
                                                          <i
                                                              title={row.info}
                                                              className="icon-info cursor-pointer"
                                                              style={{ marginLeft: '7px' }}
                                                          ></i>
                                                      )
                                                  )}
                                              </HeaderCellSort>
                                          ))
                                        : COLUMNS.map((row) => (
                                              <HeaderCell
                                                  key={row.key}
                                                  pinLeft={row.pinLeft ?? false}
                                                  style={{
                                                      ...(headerHasCustomStyles && row.style
                                                          ? row.style()
                                                          : {}),
                                                      top: EXTRA_HEADER ? '38px' : '0px',
                                                  }}
                                                  onClick={(e) => {
                                                      if (
                                                          row.infoLink &&
                                                          e.target.id === 'infoIcon'
                                                      ) {
                                                          window.open(row.infoLink);
                                                      }
                                                  }}
                                              >
                                                  {row.label}
                                                  {row.infoLink ? (
                                                      <i
                                                          id="infoIcon"
                                                          title={row.info}
                                                          className="icon-info cursor-pointer"
                                                          style={{ marginLeft: '7px' }}
                                                      ></i>
                                                  ) : (
                                                      row.info && (
                                                          <i
                                                              title={row.info}
                                                              className="icon-info cursor-pointer"
                                                              style={{ marginLeft: '7px' }}
                                                          ></i>
                                                      )
                                                  )}
                                              </HeaderCell>
                                          ))}
                                </HeaderRow>
                            </Header>

                            <Body>
                                {tableList.map((item) => (
                                    <Row
                                        key={item[uniqKey ?? 'id']}
                                        item={item}
                                        // A table that marks a row for something other than the
                                        // select feature — the row a panel elsewhere is showing,
                                        // say — says so here rather than setting a class on each
                                        // of its cells in turn.
                                        className={[
                                            typeof rowClassName === 'function'
                                                ? rowClassName(item)
                                                : rowClassName ?? '',
                                            onRowClick ? 'dt-row-clickable' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        // Read back by the body's click handler to find which
                                        // row was clicked. `Row` passes anything it does not
                                        // recognise through to the <tr>.
                                        data-row-id={
                                            onRowClick ? String(item[uniqKey ?? 'id']) : undefined
                                        }
                                    >
                                        {hasSelect && (
                                            <CellSelect
                                                item={item}
                                                disabled={
                                                    isSelectDisabled
                                                        ? isSelectDisabled(item)
                                                        : false
                                                }
                                                style={
                                                    COLUMNS[0].style ? COLUMNS[0].style(item) : {}
                                                }
                                            />
                                        )}
                                        {COLUMNS.map((column, i) => {
                                            return (
                                                <column.Component
                                                    className={`${
                                                        i === 0 ? 'first-column-styles' : ''
                                                    } ${
                                                        column.className
                                                            ? typeof column.className === 'function'
                                                                ? column.className(item)
                                                                : column.className
                                                            : ''
                                                    } `}
                                                    item={item}
                                                    key={`${column.key}-${item.id}`}
                                                    data-label={column.label}
                                                    style={column.style ? column.style(item) : {}}
                                                    pinLeft={column.pinLeft ?? false}
                                                >
                                                    <CellContent>
                                                        {column.renderCell
                                                            ? column.renderCell({
                                                                  data: item[column.data],
                                                                  item,
                                                                  allData: data?.nodes,
                                                              })
                                                            : showData(item[column.data])}
                                                    </CellContent>
                                                </column.Component>
                                            );
                                        })}
                                    </Row>
                                ))}
                            </Body>
                        </>
                    )}
                </Table>
                {(!nodes || nodes?.length === 0) && (
                    <div style={{ margin: '10px' }}>{customEmptyMessage ?? noDataMessage}</div>
                )}
                {(!onlyTable || onlyPagination) && (
                    <Pagination
                        pagination={pagination}
                        sizes={sizes}
                        nodes={nodes}
                        data={data}
                        loadMore={loadMore}
                        loadMoreLoading={loadMoreLoading}
                        showLoadMoreBtn={showLoadMoreBtn}
                        exportAll={exportAll}
                        exportAllLoading={exportAllLoading}
                        showViewAll={showViewAll}
                        viewAllLink={viewAllLink}
                    />
                )}
            </div>
        </div>
    );
}
